import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useListingAuditReport, useListingAuditStatus } from '@/hooks/useListingBuilder'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useScrollSpy } from '@/hooks/useScrollSpy'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'

import { ListingReportHeader } from './ListingReportHeader'
import { ListingReportSections } from './ListingReportSections'
import { ReportSidebar } from './ReportSidebar'
import { ReportViewToggle, type ReportViewMode } from './ReportViewToggle'
import { getVisibleSections } from './reportSectionDefs'

interface ListingReportViewProps {
  jobId: string
}

/**
 * Audit run duration in whole minutes, or undefined while a timestamp is missing.
 * The status endpoint serializer returns started_at/completed_at (ISO) which the
 * ListingAuditStatus type does not yet declare, so we read them defensively.
 */
function durationMinutes(status: unknown): number | undefined {
  const s = status as { started_at?: string | null; completed_at?: string | null } | undefined
  if (!s?.started_at || !s.completed_at) return undefined
  return Math.round((new Date(s.completed_at).getTime() - new Date(s.started_at).getTime()) / 60000)
}

/** Full audit report view: header (score dashboard) + dispatched sections. */
export function ListingReportView({ jobId }: ListingReportViewProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useListingAuditReport(jobId)
  const { data: status } = useListingAuditStatus(jobId)
  const [mode, setMode] = useState<ReportViewMode>('compact')
  const isDesktop = useMediaQuery('(min-width: 768px)')

  // Hook returns the raw report dict (Record<string, unknown>); cast at boundary.
  const report = data as unknown as ListingAuditReport | undefined
  const sidebarSections = report ? getVisibleSections(report, { mode }) : []
  const activeId = useScrollSpy(sidebarSections.map((s) => s.id))

  if (isLoading) {
    return (
      <p aria-live="polite" className="listing-report-loading text-sm text-neutral-600 dark:text-neutral-400">
        {t('listingBuilder.loadingReport')}
      </p>
    )
  }

  if (!report) return null

  return (
    <div className="listing-report-view flex flex-col gap-6">
      <ListingReportHeader report={report} jobId={jobId} durationMinutes={durationMinutes(status)} />
      <ReportViewToggle value={mode} onChange={setMode} />
      {isDesktop ? (
        <div className="flex gap-8">
          <div className="w-56 shrink-0">
            <ReportSidebar sections={sidebarSections} activeId={activeId} />
          </div>
          <div className="min-w-0 flex-1">
            <ListingReportSections report={report} mode={mode} />
          </div>
        </div>
      ) : (
        <ListingReportSections report={report} mode={mode} />
      )}
    </div>
  )
}
